/* Florame per fioristi — landing v3.0.0.468 */
(() => {
  const initFloristLanding = (root = document) => {
    const page = root.querySelector?.('[data-ff-page]') || document.querySelector('[data-ff-page]');
    if (!page || page.dataset.ffReady === 'true') return;
    page.dataset.ffReady = 'true';

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const header = page.querySelector('[data-ff-header]');
    const mobileCta = page.querySelector('[data-ff-mobile-cta]');
    const finalSection = page.querySelector('.ff-final');
    let finalVisible = false;

    const trustCloud = page.querySelector('[data-ff-trust-cloud]');
    const trustTrack = trustCloud?.querySelector('[data-ff-trust-track]');
    const trustSet = trustCloud?.querySelector('[data-ff-trust-set]');
    const trustItems = trustSet ? [...trustSet.querySelectorAll('.ff-trust-cloud__item')] : [];
    let trustMotionSignature = '';
    const syncTrustMotion = () => {
      if (!trustTrack || !trustSet || !trustItems.length) return;
      const gap = Number.parseFloat(getComputedStyle(trustSet).gap) || 34;
      const itemWidths = trustItems.map((item) => item.getBoundingClientRect().width);
      const setWidth = itemWidths.reduce((total, width) => total + width, 0) + (gap * (trustItems.length - 1));
      const signature = `${trustCloud.clientWidth}|${gap}|${itemWidths.map(Math.round).join(',')}`;
      if (signature === trustMotionSignature) return;
      trustMotionSignature = signature;

      trustTrack.style.height = `${Math.max(...trustItems.map((item) => item.offsetHeight))}px`;
      const fitsViewport = setWidth <= trustCloud.clientWidth - 48;
      const edgeInset = trustCloud.clientWidth <= 620 ? 14 : 22;
      const centeredLeft = (trustCloud.clientWidth - setWidth) / 2;
      const desktopDrift = Math.min(30, Math.max(14, centeredLeft * .12));
      const start = fitsViewport ? centeredLeft - desktopDrift : edgeInset;
      const end = fitsViewport ? centeredLeft + desktopDrift : trustCloud.clientWidth - setWidth - edgeInset;
      const distance = Math.abs(end - start);
      const duration = fitsViewport ? 10.5 : Math.max(distance / 28, 8.5);

      trustSet.style.setProperty('--ff-trust-start', `${start.toFixed(2)}px`);
      trustSet.style.setProperty('--ff-trust-end', `${end.toFixed(2)}px`);
      trustSet.style.setProperty('--ff-trust-duration', `${duration.toFixed(2)}s`);
    };

    if (trustTrack && trustSet) {
      requestAnimationFrame(syncTrustMotion);
      if ('ResizeObserver' in window) {
        const trustResizeObserver = new ResizeObserver(syncTrustMotion);
        trustItems.forEach((item) => trustResizeObserver.observe(item));
        trustResizeObserver.observe(trustCloud);
        page.ffTrustResizeObserver = trustResizeObserver;
      }
    }

    const updateChrome = () => {
      const scrolled = window.scrollY > 80;
      header?.classList.toggle('is-compact', scrolled);
      mobileCta?.classList.toggle('is-visible', window.scrollY > 520 && !finalVisible);
    };

    updateChrome();
    window.addEventListener('scroll', updateChrome, { passive: true });

    if (finalSection && 'IntersectionObserver' in window) {
      const finalObserver = new IntersectionObserver(([entry]) => {
        finalVisible = entry.isIntersecting;
        updateChrome();
      }, { threshold: .18 });
      finalObserver.observe(finalSection);
    }

    const revealItems = page.querySelectorAll('.ff-reveal');
    if (reducedMotion || !('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    } else {
      const revealObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: .12, rootMargin: '0px 0px -7% 0px' });
      revealItems.forEach((item) => revealObserver.observe(item));
    }

    page.querySelectorAll('[data-ff-availability-demo] .ff-flower').forEach((button) => {
      button.addEventListener('click', () => {
        const enabled = !button.classList.contains('is-on');
        button.classList.toggle('is-on', enabled);
        button.setAttribute('aria-pressed', String(enabled));
        const status = button.querySelector('small');
        if (status) status.textContent = enabled ? 'Disponibili' : 'Terminati';
      });
    });

    const valueStack = page.querySelector('[data-ff-value-stack]');
    if (valueStack) {
      const valueCards = [...valueStack.querySelectorAll('[data-ff-value-card]')];
      const valuePockets = [...valueStack.querySelectorAll('[data-ff-value-pocket]')];
      const syncLottie = (pocket, shouldPlay) => {
        const player = pocket.querySelector('[data-ff-value-lottie]');
        if (!player) return;
        const applyState = () => {
          if (reducedMotion || !shouldPlay) player.pause?.();
          else player.play?.();
        };
        if (typeof player.play === 'function') applyState();
        else player.addEventListener('ready', applyState, { once: true });
      };

      valueCards.forEach((card, index) => {
        card.style.setProperty('--ff-value-stack-index', index);
        valuePockets[index]?.style.setProperty('--ff-value-stack-index', index);
      });

      const setValuePocketState = (pocket, isVisible) => {
        const card = pocket.querySelector('[data-ff-value-card]');
        const wasVisible = pocket.classList.contains('is-value-pocket-visible');
        if (wasVisible === isVisible) return;
        pocket.classList.toggle('is-value-pocket-visible', isVisible);
        card?.classList.toggle('is-value-card-visible', isVisible);
        syncLottie(pocket, isVisible);
      };

      const setValuePocketFromViewport = (pocket) => {
        const bounds = pocket.getBoundingClientRect();
        const viewportHeight = Math.max(1, window.innerHeight);
        const visible = Math.max(0, Math.min(bounds.bottom, viewportHeight) - Math.max(bounds.top, 0));
        const ratio = visible / Math.max(1, bounds.height);
        const wasVisible = pocket.classList.contains('is-value-pocket-visible');
        const hasEnteredReadingArea = bounds.top <= viewportHeight * .88 && bounds.bottom >= viewportHeight * .12;
        const hasLeftReadingArea = bounds.bottom < viewportHeight * .04 || bounds.top > viewportHeight * .96;
        const shouldBeVisible = wasVisible ? !hasLeftReadingArea : (ratio >= .18 && hasEnteredReadingArea);
        setValuePocketState(pocket, shouldBeVisible);
      };

      const syncValueVisibility = () => {
        valuePockets.forEach(setValuePocketFromViewport);
      };

      let valueFrame = 0;
      const updateValueProgress = () => {
        valueFrame = 0;
        const bounds = valueStack.getBoundingClientRect();
        const travel = Math.max(1, bounds.height - window.innerHeight * .64);
        const progress = Math.min(1, Math.max(0, (-bounds.top + window.innerHeight * .32) / travel));
        valueStack.style.setProperty('--ff-value-progress', progress.toFixed(4));
        page.classList.toggle('is-value-stack-active', bounds.top <= 118 && bounds.bottom > 118);
        if (!reducedMotion && 'IntersectionObserver' in window) syncValueVisibility();
      };

      const requestValueProgress = () => {
        if (valueFrame) return;
        valueFrame = window.requestAnimationFrame(updateValueProgress);
      };

      if (reducedMotion || !('IntersectionObserver' in window)) {
        valuePockets.forEach((pocket) => setValuePocketState(pocket, true));
        valueStack.style.setProperty('--ff-value-progress', '1');
      } else {
        valueStack.classList.add('ff-value-motion-ready');
        const valueObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            setValuePocketFromViewport(entry.target);
          });
        }, { threshold: [0, .08, .18, .32, .5, .75, 1] });
        valuePockets.forEach((pocket) => valueObserver.observe(pocket));
      }

      if (!reducedMotion && 'IntersectionObserver' in window) syncValueVisibility();
      updateValueProgress();
      window.addEventListener('scroll', requestValueProgress, { passive: true });
      window.addEventListener('resize', requestValueProgress, { passive: true });
      window.addEventListener('pageshow', () => {
        if (!reducedMotion && 'IntersectionObserver' in window) syncValueVisibility();
        requestValueProgress();
      }, { passive: true });
      window.setTimeout(() => {
        if (!reducedMotion && 'IntersectionObserver' in window) syncValueVisibility();
        requestValueProgress();
      }, 1200);
    }

    const video = page.querySelector('[data-ff-video]');
    const videoToggle = page.querySelector('[data-ff-video-toggle]');
    const setVideoState = (playing) => {
      videoToggle?.classList.toggle('is-playing', playing);
      videoToggle?.setAttribute('aria-label', playing ? 'Metti in pausa la dimostrazione' : 'Riproduci la dimostrazione');
      const label = videoToggle?.querySelector('span');
      if (label) label.textContent = playing ? 'Metti in pausa' : 'Guarda il percorso';
    };

    if (video && videoToggle) {
      videoToggle.addEventListener('click', async () => {
        if (video.paused) {
          try {
            await video.play();
            setVideoState(true);
          } catch (_) {
            setVideoState(false);
          }
        } else {
          video.pause();
          setVideoState(false);
        }
      });

      video.addEventListener('play', () => setVideoState(true));
      video.addEventListener('pause', () => setVideoState(false));

      if (!reducedMotion && 'IntersectionObserver' in window) {
        const videoObserver = new IntersectionObserver(async ([entry]) => {
          if (entry.isIntersecting) {
            try { await video.play(); } catch (_) { setVideoState(false); }
          } else {
            video.pause();
          }
        }, { threshold: .48 });
        videoObserver.observe(video);
      }
    }

    const featureDialog = page.querySelector('[data-ff-feature-dialog]');
    if (featureDialog) {
      const featurePanels = [...featureDialog.querySelectorAll('[data-ff-feature-panel]')];
      const featureVideos = [...featureDialog.querySelectorAll('[data-ff-feature-video]')];
      const featureTitle = featureDialog.querySelector('[data-ff-feature-title]');
      const featureLoopTransitions = new WeakMap();
      let featureIndex = 0;
      let featureReturnFocus = null;
      let featureScrollY = 0;
      let featureBodyStyle = null;
      let featureScrollLocked = false;

      const lockFeaturePage = () => {
        if (featureScrollLocked) return;
        featureScrollY = Math.max(0, window.scrollY || window.pageYOffset || 0);
        featureBodyStyle = document.body.getAttribute('style');
        document.documentElement.classList.add('ff-feature-modal-open');
        document.body.style.position = 'fixed';
        document.body.style.top = `-${featureScrollY}px`;
        document.body.style.right = '0';
        document.body.style.left = '0';
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
        featureScrollLocked = true;
      };

      const unlockFeaturePage = () => {
        if (!featureScrollLocked) return;
        document.documentElement.classList.remove('ff-feature-modal-open');
        if (featureBodyStyle === null) document.body.removeAttribute('style');
        else document.body.setAttribute('style', featureBodyStyle);
        const previousScrollBehavior = document.documentElement.style.scrollBehavior;
        document.documentElement.style.scrollBehavior = 'auto';
        window.scrollTo(0, featureScrollY);
        window.requestAnimationFrame(() => {
          document.documentElement.style.scrollBehavior = previousScrollBehavior;
        });
        featureScrollLocked = false;
      };

      const resetFeatureLoop = (video) => {
        const loop = featureLoopTransitions.get(video);
        if (!loop) return;
        window.clearTimeout(loop.restartTimer);
        window.clearTimeout(loop.cleanupTimer);
        loop.animations.forEach((animation) => animation.cancel());
        video.closest('.ff-feature-panel__media--video')?.classList.remove('is-loop-transitioning');
        featureLoopTransitions.delete(video);
      };

      const animateFeatureLoop = (video) => {
        if (featureLoopTransitions.has(video)) return;
        const media = video.closest('.ff-feature-panel__media--video');
        const fade = media?.querySelector('.ff-feature-loop-fade');
        if (!media || !fade) return;

        media.classList.add('is-loop-transitioning');
        const timing = { duration: 820, easing: 'cubic-bezier(.45, 0, .18, 1)', fill: 'both' };
        const fadeAnimation = fade.animate([
          { opacity: 0, offset: 0 },
          { opacity: .96, offset: .43 },
          { opacity: .96, offset: .55 },
          { opacity: 0, offset: 1 }
        ], timing);
        const videoAnimation = video.animate([
          { opacity: 1, transform: 'scale(1)', offset: 0 },
          { opacity: .22, transform: 'scale(1.008)', offset: .43 },
          { opacity: .22, transform: 'scale(1.008)', offset: .55 },
          { opacity: 1, transform: 'scale(1)', offset: 1 }
        ], timing);
        const restartTimer = window.setTimeout(() => {
          if (!featureDialog.open || video.closest('[data-ff-feature-panel]')?.hidden) return;
          video.currentTime = 0;
          video.play().catch(() => {});
        }, 390);
        const cleanupTimer = window.setTimeout(() => {
          media.classList.remove('is-loop-transitioning');
          featureLoopTransitions.delete(video);
        }, 860);
        featureLoopTransitions.set(video, { animations: [fadeAnimation, videoAnimation], restartTimer, cleanupTimer });
      };

      const pauseFeatureVideos = () => {
        featureVideos.forEach((video) => {
          video.pause();
          if (video.readyState > 0) video.currentTime = 0;
          resetFeatureLoop(video);
        });
      };

      const playFeatureVideo = () => {
        if (reducedMotion || !featureDialog.open) return;
        const video = featurePanels[featureIndex]?.querySelector('[data-ff-feature-video]');
        if (!video) return;
        video.play().catch(() => {});
      };

      const setFeature = (nextIndex) => {
        featureIndex = (nextIndex + featurePanels.length) % featurePanels.length;
        pauseFeatureVideos();
        featureDialog.dataset.featureIndex = String(featureIndex);
        featurePanels.forEach((panel, index) => {
          const active = index === featureIndex;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });
        if (featureTitle) featureTitle.textContent = featurePanels[featureIndex]?.dataset.ffFeatureTitleValue || '';
        playFeatureVideo();
      };

      page.querySelectorAll('[data-ff-feature-open]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          featureReturnFocus = trigger;
          setFeature(Number(trigger.dataset.ffFeatureOpen) || 0);
          if (!featureDialog.open && featureDialog.showModal) {
            lockFeaturePage();
            try {
              featureDialog.showModal();
            } catch (error) {
              unlockFeaturePage();
              throw error;
            }
          }
          requestAnimationFrame(playFeatureVideo);
        });
      });

      featureDialog.querySelector('[data-ff-feature-close]')?.addEventListener('click', () => featureDialog.close());

      featureVideos.forEach((video) => {
        video.addEventListener('timeupdate', () => {
          if (!Number.isFinite(video.duration) || video.duration <= 0) return;
          const coverWindow = Math.min(.72, Math.max(.58, video.duration * .04));
          if (video.duration - video.currentTime <= coverWindow) animateFeatureLoop(video);
        });
        video.addEventListener('ended', () => {
          animateFeatureLoop(video);
        });
      });

      featureDialog.addEventListener('click', (event) => {
        const rect = featureDialog.getBoundingClientRect();
        const outside = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
        if (outside) featureDialog.close();
      });
      featureDialog.addEventListener('close', () => {
        pauseFeatureVideos();
        unlockFeaturePage();
        featureReturnFocus?.focus({ preventScroll: true });
      });
      featureDialog.addEventListener('cancel', pauseFeatureVideos);
    }

    page.querySelectorAll('[data-ff-dialog-open]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const dialog = document.getElementById(trigger.dataset.ffDialogOpen);
        if (dialog?.showModal) dialog.showModal();
      });
    });

    page.querySelectorAll('.ff-dialog').forEach((dialog) => {
      dialog.querySelectorAll('[data-ff-dialog-close]').forEach((button) => {
        button.addEventListener('click', () => dialog.close());
      });
      dialog.addEventListener('click', (event) => {
        const rect = dialog.getBoundingClientRect();
        const outside = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
        if (outside) dialog.close();
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initFloristLanding(document), { once: true });
  } else {
    initFloristLanding(document);
  }

  document.addEventListener('shopify:section:load', (event) => initFloristLanding(event.target));
})();
